import { COMPOSER_TRAY_OFFSET, ComposerTrayPopover, MatchedName, useComposerTrayWidth } from "#features/conversations/components/conversation-input/ComposerTray.tsx";
import type { MentionCandidate, MentionSuggestion } from "#features/conversations/components/conversation-input/mentionSuggestion.ts";
import { Avatar, Spinner } from "@hopper-ui/components";
import classNames from "classnames";
import { useId, type ReactNode, type RefObject } from "react";
import { VisuallyHidden } from "react-aria-components";

interface MentionTrayAction {
    id: string;
    name: string;
    icon: ReactNode;
    href: string;
    statusLabel: string;
}

interface Props {
    suggestion: MentionSuggestion;
    anchorRef: RefObject<HTMLElement | null>;
    listboxId: string;
    optionId: (candidate: MentionCandidate) => string;
    title: string;
    // Shown while the "@"'s first search hasn't answered.
    loadingLabel: string;
    // A quiet link after the members, as "Connect more apps" ends the + tray.
    action?: MentionTrayAction;
}

// The members an "@" can mention, in the tray the "/" skill picker uses. Focus stays in the editor, which drives the keys and
// points at the active row through aria-activedescendant, so the rows are a plain listbox with ids it can name.
export function ComposerMentionTray({ suggestion, anchorRef, listboxId, optionId, title, loadingLabel, action }: Props) {
    const width = useComposerTrayWidth(anchorRef, true);
    const headerId = useId();
    const { candidates, isLoading, activeIndex, query, select, setActiveIndex, close, dismiss } = suggestion;
    const isActionActive = activeIndex === candidates.length;

    return (
        <ComposerTrayPopover
            anchorRef={anchorRef}
            placement="top"
            offset={COMPOSER_TRAY_OFFSET}
            inlineSize={width}
            standalone={{ isOpen: true, onOpenChange: isOpen => !isOpen && close() }}
        >
            {/* The header sits in the scrolling tray, as in the other trays, so it sticks and the rows clip under it. */}
            <div className="wai-composer-tray-menu">
                <div id={headerId} className="wai-composer-tray-header">{title}</div>
                <div role="listbox" id={listboxId} aria-labelledby={headerId}>
                    {candidates.map((candidate, index) => (
                        <div
                            key={candidate.id}
                            id={optionId(candidate)}
                            role="option"
                            // Named by the member alone, as a skill row is, so the avatar's initials and the job title stay out of it.
                            aria-labelledby={`${optionId(candidate)}-name`}
                            aria-describedby={candidate.description ? `${optionId(candidate)}-description` : undefined}
                            aria-selected={index === activeIndex}
                            tabIndex={-1}
                            className={classNames("wai-composer-tray-item", { "wai-composer-tray-item--active": index === activeIndex })}
                            // Picked on press, before the editor would lose focus to the row.
                            onMouseDown={event => {
                                event.preventDefault();
                                select(candidate);
                            }}
                            // Not on enter: Chrome fires that on the row under a resting pointer when the tray opens, with no movement.
                            onPointerMove={() => setActiveIndex(index)}
                        >
                            <span className="wai-composer-tray-item-icon" aria-hidden>
                                <Avatar data-private name={candidate.label} size="xs" src={candidate.imageUrl} />
                            </span>
                            <span id={`${optionId(candidate)}-name`} className="wai-composer-tray-item-name">
                                <MatchedName name={candidate.label} match={query} />
                            </span>
                            {candidate.description && (
                                <span id={`${optionId(candidate)}-description`} title={candidate.description} className="wai-composer-tray-item-description">
                                    {candidate.description}
                                </span>
                            )}
                        </div>
                    ))}
                    {action && candidates.length > 0 && (
                        <a
                            id={action.id}
                            role="option"
                            aria-selected={isActionActive}
                            tabIndex={-1}
                            href={action.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classNames("wai-composer-tray-item", { "wai-composer-tray-item--active": isActionActive })}
                            onMouseDown={event => event.preventDefault()}
                            // Opened here rather than by the link: dropping the "@query" unmounts it mid-click.
                            onClick={event => {
                                event.preventDefault();
                                window.open(action.href, "_blank", "noopener");
                                dismiss();
                            }}
                            onPointerMove={() => setActiveIndex(candidates.length)}
                        >
                            <span className="wai-composer-tray-item-icon" aria-hidden>{action.icon}</span>
                            <span className="wai-composer-tray-item-quiet">
                                {action.name}
                                <VisuallyHidden>, {action.statusLabel}</VisuallyHidden>
                            </span>
                        </a>
                    )}
                </div>
                {/* A member row's height, so the tray keeps its size when the members replace it. */}
                {candidates.length === 0 && isLoading && (
                    <div role="status" className="wai-composer-tray-item wai-composer-tray-item--loading">
                        <span className="wai-composer-tray-item-icon" aria-hidden>
                            <Spinner size="sm" aria-label={loadingLabel} />
                        </span>
                        <span className="wai-composer-tray-item-quiet">{loadingLabel}</span>
                    </div>
                )}
            </div>
        </ComposerTrayPopover>
    );
}
